import React, { useCallback, useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import classNames from 'classnames'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { type CustomIntegrationAuthType } from '../../../models/CustomIntegration'
import { useCreateCustomIntegrationMutation } from '../../../store/customIntegration/customIntegration'
import { addToast } from '../../../store/toast/toastSlice'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { IntegrationManagementHeader } from '../../Integration/IntegrationManagement/IntegrationManagementHeader'
import { WizardStepCard } from '../components/WizardStepCard'
import { CustomIntegrationDetailsFormFields } from '../components/CustomIntegrationDetailsFormFields'
import { AuthTypeSelector } from '../components/AuthTypeSelector'
import { AuthDetailsFormFields } from '../components/AuthDetailsFormFields'
import {
  type AuthFormState,
  buildCreatePayload,
  createAuthFormState,
  type DetailsFormData,
  hasAuthDetailsStep,
  INITIAL_DETAILS,
  isAuthDetailsValid,
  isDetailsValid,
  isSameFormValue,
} from '../form/customIntegrationForm'
import { customIntegrationDetailsPath } from '../constants'
import { AUTH_DETAILS_SUBTITLE } from '../form/authTypeOptions'
import { UnsavedChangesWarningBar } from '../../../components/FormSaveBar/FormSaveBar'
import { useUnsavedChangesGuard } from '../../../common/useUnsavedChangesGuard'
import { Upsell } from '../../Plan/DeltaTrial/Upsell/Upsell'
import { DELTA_TRIAL_FEATURES } from '../../Plan/DeltaTrial/DeltaTrialFeatures'
import { useCustomIntegrationQuota } from '../useCustomIntegrationQuota'
import styles from './CreateCustomIntegrationWizard.module.scss'

type StepId = 'details' | 'authType' | 'authDetails' | 'allSet'

const MCP_LOGO_URL = `${import.meta.env.BASE_URL.replace(/\/$/, '')}/oauth-logos/mcp_logo.svg`

const stepsFor = (authType: CustomIntegrationAuthType | null): StepId[] => [
  'details',
  'authType',
  ...(hasAuthDetailsStep(authType) ? (['authDetails'] as StepId[]) : []),
  'allSet',
]

const resetAuthForm = (state: AuthFormState, authType: CustomIntegrationAuthType | null): AuthFormState => {
  if (!hasAuthDetailsStep(authType)) {
    return state
  }
  const initial = createAuthFormState()
  if (authType === 'oauth2') {
    return { ...state, oauth2: initial.oauth2 }
  }
  if (authType === 'oauth2Dcr') {
    return { ...state, oauth2Dcr: initial.oauth2Dcr }
  }
  return { ...state, apiKey: initial.apiKey }
}

export const CreateCustomIntegrationWizard: React.FC = () => {
  useDocumentTitle('Add an external MCP server')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const [createCustomIntegration, { isLoading: isCreating }] = useCreateCustomIntegrationMutation()
  const { customIntegrationsEnabled, isFetching: quotaIsFetching, quotaSuccess } = useCustomIntegrationQuota()

  const [details, setDetails] = useState<DetailsFormData>(INITIAL_DETAILS)
  const [committedDetails, setCommittedDetails] = useState<DetailsFormData | null>(null)
  const [authType, setAuthType] = useState<CustomIntegrationAuthType | null>(null)
  const [committedAuthType, setCommittedAuthType] = useState<CustomIntegrationAuthType | null>(null)
  const [authForms, setAuthForms] = useState<AuthFormState>(createAuthFormState)
  const [initialAuthForms] = useState<AuthFormState>(authForms)
  const [committedAuthForms, setCommittedAuthForms] = useState<AuthFormState | null>(null)
  const [revealedUpTo, setRevealedUpTo] = useState<StepId>('details')
  const [createdId, setCreatedId] = useState<string | null>(null)

  const { portalRef } = useContext(HeaderContext)
  const [headerContainer, setHeaderContainer] = useState<HTMLElement | null>(null)
  useEffect(() => {
    if (portalRef && portalRef.current) {
      setHeaderContainer(portalRef.current)
    }
  }, [portalRef])

  const authTypeDirty = committedAuthType !== null && authType !== committedAuthType
  const steps = stepsFor(committedAuthType)
  const revealedStep: StepId = authTypeDirty ? 'authType' : revealedUpTo
  const visibleSteps = steps.slice(0, steps.indexOf(revealedStep) + 1)
  const isVisible = (step: StepId) => visibleSteps.includes(step)
  const isLastRevealed = (step: StepId) => !authTypeDirty && step === revealedUpTo

  const detailsValid = isDetailsValid(details)
  const detailsDirty = committedDetails === null || !isSameFormValue(details, committedDetails)
  const authDetailsValid = committedAuthType !== null && isAuthDetailsValid(committedAuthType, authForms)
  const authDetailsDirty = committedAuthForms === null || !isSameFormValue(authForms, committedAuthForms)

  const handleDetailsSubmit = useCallback(() => {
    setCommittedDetails(details)
    if (revealedUpTo === 'details') {
      setRevealedUpTo('authType')
    }
  }, [details, revealedUpTo])

  const handleAuthTypeSubmit = useCallback(() => {
    if (authType === null) {
      return
    }
    const typeChanged = committedAuthType !== null && committedAuthType !== authType
    const nextForms = typeChanged ? resetAuthForm(authForms, committedAuthType) : authForms
    setAuthForms(nextForms)
    setCommittedAuthType(authType)
    setCommittedAuthForms(null)
    setRevealedUpTo(hasAuthDetailsStep(authType) ? 'authDetails' : 'allSet')
  }, [authType, committedAuthType, authForms])

  const handleAuthDetailsSubmit = useCallback(() => {
    setCommittedAuthForms(authForms)
    if (revealedUpTo === 'authDetails') {
      setRevealedUpTo('allSet')
    }
  }, [authForms, revealedUpTo])

  const handleCreate = useCallback(async () => {
    if (committedDetails === null || committedAuthType === null) {
      return
    }
    try {
      const created = await createCustomIntegration({
        accountId,
        payload: buildCreatePayload(committedDetails, committedAuthType, committedAuthForms ?? authForms),
      }).unwrap()
      setCreatedId(created.id)
    } catch {
      dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to create the MCP connection. Please try again.'] }))
    }
  }, [committedDetails, committedAuthType, committedAuthForms, authForms, accountId, createCustomIntegration, dispatch])

  useEffect(() => {
    if (createdId !== null) {
      navigate(customIntegrationDetailsPath(createdId))
    }
  }, [createdId, navigate])

  const isDirty = !isSameFormValue(details, INITIAL_DETAILS) || authType !== null || !isSameFormValue(authForms, initialAuthForms) || revealedUpTo !== 'details'
  const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesGuard(isDirty, isCreating || createdId !== null)

  const header = headerContainer ? createPortal(<IntegrationManagementHeader backTarget='app' />, headerContainer) : null

  if (quotaIsFetching) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    )
  }
  if (quotaSuccess && !customIntegrationsEnabled) {
    return (
      <>
        {header}
        <Upsell feature={DELTA_TRIAL_FEATURES['customIntegrations']} />
      </>
    )
  }

  return (
    <div className={classNames('mx-auto zocks-px-1.25 py-4 d-flex flex-column min-vh-100', styles.container)}>
      {header}
      <h3 className='d-flex align-items-center gap-3 mb-0'>
        <img src={MCP_LOGO_URL} alt='' className={styles.logo} />
        Add an external MCP server
      </h3>

      <WizardStepCard
        icon='bi-file-text'
        title='Details'
        subtitle='Name your external MCP and provide the URL for it.'
        action={{
          label: isLastRevealed('details') ? 'Continue' : 'Update',
          disabled: !detailsValid || !detailsDirty,
          showEnterHint: isLastRevealed('details'),
        }}
        onSubmit={handleDetailsSubmit}
      >
        <CustomIntegrationDetailsFormFields data={details} onChange={setDetails} />
      </WizardStepCard>

      {isVisible('authType') ? (
        <WizardStepCard
          icon='bi-shield-lock'
          title='Authorization type'
          subtitle='Select the type of authorization the MCP server uses.'
          action={{
            label: isLastRevealed('authType') ? 'Continue' : 'Update',
            disabled: authType === null || (!isLastRevealed('authType') && !authTypeDirty),
            showEnterHint: isLastRevealed('authType'),
          }}
          onSubmit={handleAuthTypeSubmit}
        >
          <AuthTypeSelector value={authType} onChange={setAuthType} />
        </WizardStepCard>
      ) : null}

      {isVisible('authDetails') && committedAuthType !== null ? (
        <WizardStepCard
          icon='bi-journal-text'
          title='Authorization Details'
          subtitle={AUTH_DETAILS_SUBTITLE}
          action={{
            label: isLastRevealed('authDetails') ? 'Continue' : 'Update',
            disabled: !authDetailsValid || !authDetailsDirty,
            showEnterHint: isLastRevealed('authDetails'),
          }}
          onSubmit={handleAuthDetailsSubmit}
        >
          <AuthDetailsFormFields authType={committedAuthType} state={authForms} onChange={setAuthForms} />
        </WizardStepCard>
      ) : null}

      {isVisible('allSet') ? (
        <WizardStepCard
          icon='bi-patch-check'
          title='All is set'
          subtitle="When you're ready, click Create to establish the MCP connection."
          action={{ label: 'Create MCP connection', disabled: false, loading: isCreating, showEnterHint: true, align: 'center' }}
          onSubmit={handleCreate}
        />
      ) : null}
      {isBlocked ? <UnsavedChangesWarningBar onConfirmLeave={confirmLeave} onCancelLeave={cancelLeave} /> : null}
    </div>
  )
}
